import React, { useEffect, useRef } from "react";
import { useTranslation } from "@helpers/translate";
import ScrollArrowIcon from "@components/library/Icons/ScrollArrowIcon";
import FeatureCard from "../cards/FeatureCard";
import SecurityCard from "../cards/SecurityCard";
import FeatureCardButton from "../buttons/FeatureCardButton";
import iphoneImage from "@assets/img/iphone.png";
import macOnTableImage from "@assets/img/mac-on-table.png";
import macOnTableMobileImage from "@assets/img/mac-on-table-mobile.png";
import "./WhatWeOffer.sass";

const WhatWeOffer = ({ onShieldElementChange }) => {
  const { __ } = useTranslation();

  const trackRef = useRef(null);
  const stickyContainerRef = useRef(null);
  const cardsRef = useRef(null);
  const titleRef = useRef(null);
  const shieldAnchorRef = useRef(null);
  const securityContentRef = useRef(null);

  const stateRef = useRef("default");

  useEffect(() => {
    onShieldElementChange?.(shieldAnchorRef.current);

    return () => {
      onShieldElementChange?.(null);
    };
  }, [onShieldElementChange]);

  useEffect(() => {
    const checkIsMobile = () => {
      return window.matchMedia("(max-width: 1023px)").matches;
    };

    const resetStyles = () => {
      if (stickyContainerRef.current) {
        stickyContainerRef.current.style.cssText = "";
      }

      if (trackRef.current) {
        trackRef.current.style.cssText = "";
      }

      if (cardsRef.current) {
        cardsRef.current.style.transform = "";

        const cards = cardsRef.current.querySelectorAll(".what-we-offer__card");

        cards.forEach((card) => {
          card.style.cssText = "";
        });
      }

      if (titleRef.current) {
        titleRef.current.style.cssText = "";
      }

      /*
       * Не очищаємо style.cssText у shieldAnchor.
       * Усередині нього в цей момент може
       * знаходитися єдиний shared img.
       */
      if (securityContentRef.current) {
        securityContentRef.current.style.cssText = "";
      }

      const featureColumns = document.querySelectorAll(
        ".features-flex__column",
      );

      featureColumns.forEach((column) => {
        column.style.opacity = "";
      });

      stateRef.current = "default";
    };

    const setLastCardMargin = () => {
      if (checkIsMobile() || !cardsRef.current) {
        return;
      }

      const lastCard = cardsRef.current.lastElementChild;

      if (!lastCard) return;

      const viewportWidth = document.documentElement.clientWidth;

      const cardWidth = lastCard.offsetWidth;

      const exactMargin = viewportWidth / 2 - cardWidth / 2;

      lastCard.style.marginRight = `${exactMargin}px`;
    };

    const handleMobileScroll = () => {
      if (!trackRef.current || !cardsRef.current) {
        return;
      }

      const viewportHeight = window.innerHeight;

      const sectionRect = trackRef.current.getBoundingClientRect();

      const allCards = cardsRef.current.querySelectorAll(
        ".what-we-offer__card",
      );

      const lastCard = cardsRef.current.lastElementChild;

      const startAnimation = viewportHeight;

      const endAnimation = viewportHeight * 0.2;

      let progress = 0;

      if (sectionRect.bottom < startAnimation) {
        progress =
          (startAnimation - sectionRect.bottom) /
          (startAnimation - endAnimation);
      }

      progress = Math.min(Math.max(progress, 0), 1);

      const fadeValue = Math.max(1 - progress * 3, 0);

      if (titleRef.current) {
        titleRef.current.style.opacity = `${fadeValue}`;
      }

      allCards.forEach((card) => {
        if (card !== lastCard) {
          card.style.opacity = `${fadeValue}`;

          card.style.backgroundColor = "";
          card.style.boxShadow = "";

          return;
        }

        card.style.boxShadow = progress > 0.05 ? "none" : "";

        if (securityContentRef.current) {
          securityContentRef.current.style.opacity = `${fadeValue}`;
        }
      });
    };

    const handleDesktopScroll = () => {
      if (
        !trackRef.current ||
        !stickyContainerRef.current ||
        !cardsRef.current
      ) {
        return;
      }

      const viewportHeight = window.innerHeight;

      const track = trackRef.current;

      const stickyContainer = stickyContainerRef.current;

      const cards = cardsRef.current;

      const trackRect = track.getBoundingClientRect();

      const scrollDistance = trackRect.height - viewportHeight;

      if (trackRect.top <= 0 && trackRect.bottom >= viewportHeight) {
        if (
          stateRef.current !== "fixed" ||
          stickyContainer.style.width !== `${trackRect.width}px`
        ) {
          stickyContainer.style.position = "fixed";

          stickyContainer.style.top = "0";

          stickyContainer.style.left = `${trackRect.left}px`;

          stickyContainer.style.width = `${trackRect.width}px`;

          stickyContainer.style.bottom = "auto";

          stickyContainer.style.zIndex = "10";

          stateRef.current = "fixed";
        }
      } else if (trackRect.bottom < viewportHeight) {
        if (stateRef.current !== "bottom") {
          stickyContainer.style.position = "absolute";

          stickyContainer.style.top = "auto";

          stickyContainer.style.left = "0";

          stickyContainer.style.width = "100%";

          stickyContainer.style.bottom = "0";

          stateRef.current = "bottom";
        }
      } else if (stateRef.current !== "default") {
        stickyContainer.style.position = "absolute";

        stickyContainer.style.top = "0";
        stickyContainer.style.left = "0";
        stickyContainer.style.width = "100%";

        stickyContainer.style.bottom = "auto";

        stateRef.current = "default";
      }

      let scrolled = -trackRect.top;

      scrolled = Math.min(Math.max(scrolled, 0), Math.max(scrollDistance, 0));

      const transitionStartPoint = 0.75;

      const totalProgress = scrollDistance > 0 ? scrolled / scrollDistance : 0;

      const stickyWidth = stickyContainer.offsetWidth;

      const maxTranslate = Math.max(cards.scrollWidth - stickyWidth, 0);

      const featureColumns = document.querySelectorAll(
        ".features-flex__column",
      );

      if (totalProgress <= transitionStartPoint) {
        const horizontalProgress = totalProgress / transitionStartPoint;

        const translateX = maxTranslate * horizontalProgress;

        cards.style.transform = `translate3d(-${translateX}px, 0, 0)`;

        if (shieldAnchorRef.current) {
          shieldAnchorRef.current.style.transform =
            "translate3d(0, 0, 0) scale(1)";
        }

        if (securityContentRef.current) {
          securityContentRef.current.style.opacity = "1";
        }

        if (titleRef.current) {
          titleRef.current.style.transform = "translateY(0)";

          titleRef.current.style.opacity = "1";
        }

        const allCards = document.querySelectorAll(".what-we-offer__card");

        allCards.forEach((card) => {
          card.style.opacity = "1";
          card.style.backgroundColor = "";
          card.style.boxShadow = "";
        });

        featureColumns.forEach((column) => {
          column.style.opacity = "0";
        });

        return;
      }

      cards.style.transform = `translate3d(-${maxTranslate}px, 0, 0)`;

      const transitionProgress =
        (totalProgress - transitionStartPoint) / (1 - transitionStartPoint);

      if (securityContentRef.current) {
        securityContentRef.current.style.opacity = `${
          1 - transitionProgress * 3
        }`;
      }

      const otherCards = document.querySelectorAll(
        ".what-we-offer__card:not(:last-child)",
      );

      otherCards.forEach((card) => {
        card.style.opacity = `${1 - transitionProgress * 3}`;
      });

      if (titleRef.current) {
        const titleMoveUp = transitionProgress * 100;

        titleRef.current.style.transform = `translateY(-${titleMoveUp}px)`;

        titleRef.current.style.opacity = "1";
      }

      if (shieldAnchorRef.current) {
        let targetScale = 2;

        if (window.innerWidth >= 1024 && window.innerWidth <= 1200) {
          targetScale = 1.7;
        } else if (window.innerWidth > 1920) {
          const currentWidth = shieldAnchorRef.current.offsetWidth;

          if (currentWidth > 0) {
            targetScale = 580 / currentWidth;
          }
        }

        const scale = 1 + (targetScale - 1) * transitionProgress;

        shieldAnchorRef.current.style.transform = `scale(${scale})`;
      }

      const delayPoint = 0.6;

      let columnOpacity = (transitionProgress - delayPoint) / (1 - delayPoint);

      columnOpacity = Math.min(Math.max(columnOpacity, 0), 1);

      featureColumns.forEach((column) => {
        column.style.opacity = `${columnOpacity}`;
      });
    };

    const handleScroll = () => {
      if (checkIsMobile()) {
        handleMobileScroll();
        return;
      }

      handleDesktopScroll();
    };

    const handleResize = () => {
      resetStyles();
      setLastCardMargin();
      handleScroll();
    };

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    window.addEventListener("resize", handleResize);

    setLastCardMargin();
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);

      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <section id="benefit" className="what-we-offer-track" ref={trackRef}>
      <div className="what-we-offer-sticky" ref={stickyContainerRef}>
        <div className="what-we-offer">
          <h1 className="what-we-offer__title text-shark" ref={titleRef}>
            {__("main.whatWeOffer.title")}
          </h1>

          <div className="what-we-offer__container">
            <div className="what-we-offer__cards" ref={cardsRef}>
              <div className="what-we-offer__scroll-link">
                <span className="what-we-offer__scroll-link-text text-shark">
                  {__("main.whatWeOffer.scrollJourney")}
                </span>

                <ScrollArrowIcon className="what-we-offer__scroll-link-icon" />
              </div>

              <div className="what-we-offer__card">
                <FeatureCard
                  titleLine1={__("main.whatWeOffer.tradeFutures1")}
                  titleLine2={__("main.whatWeOffer.tradeFutures2")}
                  description={__("main.whatWeOffer.tradeFuturesDesc")}
                  button={FeatureCardButton}
                  buttonText={__("main.whatWeOffer.tradeFuturesButton")}
                  imageSrc={iphoneImage}
                  imageAlt="Trade Futures on Binify"
                  variant="frictionless"
                />
              </div>

              <div className="what-we-offer__card">
                <FeatureCard
                  titleLine1={__("main.whatWeOffer.proGrade1")}
                  titleLine2={__("main.whatWeOffer.proGrade2")}
                  description={__("main.whatWeOffer.proGradeDesc")}
                  button={FeatureCardButton}
                  buttonText={__("main.whatWeOffer.proGradeButton")}
                  backgroundImage={macOnTableImage}
                  backgroundImageMobile={macOnTableMobileImage}
                  variant="pro-grade"
                />
              </div>

              <div className="what-we-offer__card">
                <SecurityCard
                  shieldAnchorRef={shieldAnchorRef}
                  contentRef={securityContentRef}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatWeOffer;
